import { sanitize } from 'isomorphic-dompurify';

import { STRAPI_URL } from '@/constants';
import { defaultLanguages } from '@/features/site-language/helpers/defaultLanguages';
import type { ImageType } from '@/types/Image';

const throwError = (url: string, response: any) => {
    console.error('ERROR: Failed to get Strapi data:', url, response.status, response.statusText);
    throw new Error(`ERROR: Failed to get Strapi data:\n${url}\n`);
};

export const processSocials = async (socials: any) =>
    Array.isArray(socials) && socials.length > 0
        ? await Promise.all(
              socials.map(async ({ attributes }: { attributes: any }) => ({
                  name: attributes.name,
                  url: attributes.url,
                  icon: await processImage(attributes.icon),
              }))
          )
        : [];

export const processImage = async (image: any) => {
    let data = null;
    const img: ImageType = {
        src: '',
        height: 0,
        width: 0,
    };

    if (image.data === null) return img;

    if (Object.hasOwn(image.data, 'attributes')) {
        data = image.data.attributes;
    } else if (image.data.length > 0 && Object.hasOwn(image.data[0], 'attributes')) {
        data = image.data[0].attributes;
    }

    if (data.url.includes('.svg')) {
        const response = await fetch(data.url);
        const svg = await response.text();
        const dom = await sanitize(svg);

        img.dom = dom;
    }

    img.src = data?.url || '';
    img.height = data?.height || 0;
    img.width = data?.width || 0;

    return await img;
};

export const processLinks = (links: any) =>
    links.map(({ id = -1, text = '', url = '' }) => ({ id, text, url }));

export const processFooterLinks = (links: any) =>
    links.map(({ id = -1, text = '', url = '', is_bold_required: isBoldRequired = false }) => ({
        id,
        text,
        url,
        isBoldRequired,
    }));

export const processSocialAccounts = async (socials: any) => {
    if (!socials) return [];
    return await Promise.all(
        socials.map(async (social: any) => ({
            id: social.id,
            name: social.attributes.name || '',
            url: social.attributes.url || '',
            icon: await processImage(social.attributes.icon),
        }))
    );
};

export const processLinkedImages = async (items: any) => {
    return await Promise.all(
        items.map(
            async ({
                id,
                image,
                text = '',
                url = '',
                details = '',
            }: {
                id: number;
                text: string;
                url: string;
                details: string;
                image: any;
            }) => ({ id, text, url, details, image: await processImage(image) })
        )
    );
};

export const processPods = async (pods: any) => {
    const processedPods = [];

    for (const pod of pods) {
        switch (pod.__component) {
            case 'pods.gallery':
                processedPods.push({
                    id: pod.id,
                    type: 'gallery',
                    images: await Promise.all(
                        pod.image.map(async ({ id = '', description = '', media = '' }) => ({
                            id: id || '',
                            description: description || '',
                            image: await processImage(media),
                        }))
                    ),
                });
                break;
            case 'pods.text-block':
                processedPods.push({
                    id: pod.id,
                    type: 'text',
                    text: pod.text || '',
                });
                break;
            case 'pods.trailer':
                processedPods.push({
                    id: pod.id,
                    type: 'trailer',
                    url: pod.url || '',
                    description: pod.description || '',
                });
                break;
        }
    }

    return processedPods;
};
export const processSignUpBonus = async (bonus: any) => ({
    details: bonus?.details || '',
    logged_out_cta: bonus?.logged_out_cta || '',
    logged_in_cta: bonus?.logged_in_cta || '',
    image: await processImage(bonus?.image),
});

export const processSignUpBonusSection = async (section: any) => ({
    title: section?.title,
    introduction: section?.introduction,
    logged_out_cta: section?.logged_out_cta,
    logged_in_cta: section?.logged_in_cta,
    steps:
        section?.steps.map(({ instruction = '', details = '' }) => ({
            instruction,
            details,
        })) || [],
    image: await processImage(section?.image),
});
export const strapiFetch = async (url: string, lang: string): Promise<any> => {
    const language =
        defaultLanguages.find(
            (language) => language.path.toLowerCase().replace('/', '') === lang.toLowerCase()
        )?.iso || 'en';
    const localizedURL = `${STRAPI_URL}${url}${url.includes('?') ? '&' : '?'}locale=${language}`;

    // Do an initial call with a localized URL to see if we have content.
    // The Strapi-Response-Format: v4 header keeps the v4 response shape during
    // the Strapi v5 migration so this consumer can ship before the Strapi cutover.
    let response: any = await fetch(localizedURL, {
        next: { revalidate: 300 },
        headers: { 'Strapi-Response-Format': 'v4' },
    });

    if (response.ok) {
        response = await response.json();

        // if the response is an empty object and language is not 'en', it will refetch and fallback to english content.
        if (Object.keys(response).length === 0 && lang !== 'en') {
            return strapiFetch(url, 'en');
        }
    } else {
        // if we do not have any data for a given locale
        // re-request the data with the default locale, which is English.
        if (lang !== 'en') {
            return strapiFetch(url, 'en');
        } else {
            throwError(localizedURL, response);
        }
    }

    let { data } = response;

    data =
        data?.attributes ||
        data?.map((datum: any) => ({
            ...datum.attributes,
            id: datum.id,
            // v5 emits `documentId` at the top level (next to `id`), NOT inside
            // `attributes`. Surface it so callers can use it for findOne URLs,
            // since v5's `/<plural>/:documentId` route doesn't accept the
            // numeric `id` anymore. v4 responses omit documentId, which is
            // fine — the value will simply be undefined pre-cutover.
            documentId: datum.documentId,
        })) ||
        {};

    return data;
};

export const getNotFoundRoute = async ({ language }: { language: string }) => {
    const response = await strapiFetch('/error-route', language);

    const data = {
        title: response.title || '',
        return_home: response.return_home || '',
    };

    return data;
};

export const getPages = async ({ language }: { language: string }) => {
    const response = await strapiFetch('/pages', language);

    const data = [
        ...((await Promise.all(
            response.map(async (page: any) => ({
                ...page,
                title: page.title,
                description: page.description,
                published_date: page.published_date,
                slug: page.slug,
            }))
        )) || []),
    ];

    return data;
};

export const getPagesRoute = async ({ slug, language }: { slug: string; language: string }) => {
    const response = await strapiFetch(`/pages?filters[slug][$eq]=${slug}`, language);

    const page = response[0];

    const data = {
        title: page.title,
        description: page.description,
        published_date: page.published_date,
        slug: page.slug,
        cover_image: await processImage(page.cover_image),
        body_content: await processPods(page.body_content),
    };

    return data;
};

export const getSiteMetadata = async ({ language }: { language: string }) => {
    const response = await strapiFetch('/seo', language);

    const data = {
        site_name: response?.site_name || '',
        description: response?.description || '',
        twitter: response?.twitter_username || '',
        image: await processImage(response?.social_image),
    };

    return data;
};
